import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useLocation, useNavigate, useParams } from 'react-router-dom';

import useOffers from 'api/useOffers';
import usePermissions from 'api/usePermissions';
import usePricing from 'api/usePricing';
import { Offer } from 'common/contracts';
import { EPermissionAction, EPermissionGroup } from 'common/permissions.types';
import {
  OfferType,
  SortingDirection,
  SortingOffersValue
} from 'constants/enums';
import { usePaginationFromURL } from 'hooks/usePaginationFromURL';
import { useTableSorting } from 'hooks/useTableSorting';
import { useUserPermissions } from 'hooks/useUserPermissions';
import { EToasterStatus, showToast } from 'utils/showToast';

import { CLIENT_SIDE_SORT_VALUES } from '../Offer.consts';
import { IOfferActionHandlers, IOfferPermissions } from '../Offer.types';
import { applySorting, getApiSorting } from '../Offer.utils';

interface IUseOfferTableProps {
  offerType: OfferType;
  offerSubType?: string;
  offerLabel: string;
  needsPricings?: boolean;
}

interface IProcessedOffers {
  filtered: Offer[];
  sorted: Offer[];
  totalCount: number;
  totalPages: number;
  buttonText: string;
}

interface IUseOfferTableReturn {
  // Data
  getOffers: ReturnType<typeof useOffers>['getOffers'];
  pricingsResult: unknown;
  isPricingsLoading: boolean;
  // Pagination
  currentPage: number;
  setCurrentPage: (page: number) => void;
  rowsPerPage: number;
  // Sorting
  sorting: {
    sortValue: SortingOffersValue | null;
    direction: SortingDirection;
  };
  updateSorting: (
    sortValue: SortingOffersValue,
    direction: SortingDirection | null
  ) => void;
  // Filters
  externalIdFilter: string;
  setExternalIdFilter: (value: string) => void;
  nameFilter: string;
  setNameFilter: (value: string) => void;
  // Permissions
  permissions: IOfferPermissions;
  canManageOffers: boolean;
  canUserEdit: boolean;
  // Actions
  actions: IOfferActionHandlers;
  // Modals
  selectedOffer: string | null;
  isDeleteDialogOpen: boolean;
  showLimitedEditModal: boolean;
  handleDeleteOffer: () => void;
  handleCreate: () => void;
  handleCloseLimitedEditModal: () => void;
  handleCloseDeleteDialog: () => void;
  // Navigation
  navigate: ReturnType<typeof useNavigate>;
  // Raw API methods
  toggleOfferStatus: ReturnType<typeof useOffers>['toggleOfferStatus'];
  deleteOffer: ReturnType<typeof useOffers>['deleteOffer'];
  updateCollectedIndex: ReturnType<typeof useOffers>['updateCollectedIndex'];
  // Offers
  offers: Offer[];
  // Offer pipeline
  processOffers: (offers: Offer[]) => IProcessedOffers;
}

/**
 * Shared hook that encapsulates the common table orchestration logic
 * for all 4 offer types: pagination, sorting, permissions, filters,
 * action handlers, and delete logic.
 *
 * @example
 * const { getOffers, columns, actions, permissions } = useOfferTable({
 *   offerType: OfferType.BUNDLE,
 *   offerLabel: 'Bundle',
 *   needsPricings: true
 * });
 */
export const useOfferTable = ({
  offerType,
  offerSubType,
  offerLabel,
  needsPricings = true
}: IUseOfferTableProps): IUseOfferTableReturn => {
  const { publisherId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { canUserEdit } = usePermissions();
  const { isActionEnabled } = useUserPermissions();
  const { page, rows } = usePaginationFromURL('offers');
  const { getPricings } = usePricing(publisherId);

  const [currentPage, setCurrentPage] = useState(page);
  const [rowsPerPage] = useState(rows);
  const { sorting, updateSorting } = useTableSorting(
    SortingOffersValue.CREATED_AT
  );

  const apiSorting: {
    sortValue: SortingOffersValue;
    direction: SortingDirection;
  } = useMemo(() => getApiSorting(sorting), [sorting]);

  const { getOffers, deleteOffer, toggleOfferStatus, updateCollectedIndex } =
    useOffers({
      offerType,
      offerSubType,
      customPagination: { currentPage, rowsPerPage },
      sorting: apiSorting
    });

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState<string | null>(null);
  const [externalIdFilter, setExternalIdFilter] = useState('');
  const [nameFilter, setNameFilter] = useState('');
  const [showLimitedEditModal, setShowLimitedEditModal] = useState(false);

  // --- Permissions ---

  const canManageOffers = useMemo(
    (): boolean =>
      isActionEnabled(
        null,
        EPermissionGroup.OFFERS,
        EPermissionAction.MANAGE,
        publisherId
      ),
    [publisherId, isActionEnabled]
  );

  const canUpdateStatusDesign = useMemo(
    (): boolean =>
      isActionEnabled(
        null,
        EPermissionGroup.OFFERS,
        EPermissionAction.UPDATE_OFFER_STATUS_DESIGN,
        publisherId
      ),
    [publisherId, isActionEnabled]
  );

  const permissions = useMemo(
    (): IOfferPermissions => ({
      canUserEdit,
      canManageOffers,
      canUpdateStatusDesign
    }),
    [canUserEdit, canManageOffers, canUpdateStatusDesign]
  );

  // --- URL sync (always) ---

  const isInitialMount = useRef(true);

  useEffect(() => {
    const queryParams = new URLSearchParams();
    queryParams.set('page', String(currentPage));
    queryParams.set('rows', String(rowsPerPage));

    if (sorting.sortValue !== null) {
      queryParams.set('sortValue', sorting.sortValue);
      queryParams.set('direction', sorting.direction);
    }

    navigate(`${location.pathname}?${queryParams.toString()}`, {
      replace: true
    });
  }, [currentPage, rowsPerPage, sorting]);

  // --- API refetch (server-side sorts and pagination only) ---

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (
      sorting.sortValue !== null &&
      CLIENT_SIDE_SORT_VALUES.has(sorting.sortValue)
    ) {
      return;
    }

    getOffers.refetch();
    if (needsPricings) {
      getPricings.refetch();
    }
  }, [currentPage, rowsPerPage, sorting]);

  // --- Delete handler ---

  const handleDeleteOffer = useCallback((): void => {
    if (!selectedOffer) {
      return;
    }
    deleteOffer.mutate(selectedOffer, {
      onSuccess: () => {
        showToast({
          message: `${offerLabel} deleted successfully`,
          status: EToasterStatus.SUCCESS
        });
        getOffers.refetch();
      },
      onError: (e: unknown) => {
        const axiosError = e as { response?: { data?: { message?: string } } };
        showToast({
          message: `Failed to delete ${offerLabel.toLowerCase()}`,
          status: EToasterStatus.ERROR,
          description: axiosError.response?.data?.message ?? ''
        });
      },
      onSettled: () => {
        setIsDeleteDialogOpen(false);
        setSelectedOffer(null);
      }
    });
  }, [selectedOffer, deleteOffer, getOffers, offerLabel]);

  // --- Actions ---

  const actions = useMemo(
    (): IOfferActionHandlers => ({
      onEdit: (offerId: string): void => {
        navigate(`./form/${offerId}`);
      },
      onLimitedEdit: (offerId: string): void => {
        setSelectedOffer(offerId);
        setShowLimitedEditModal(true);
      },
      onToggleStatus: (offerId: string, active: boolean): void => {
        toggleOfferStatus.mutate(
          { active: !active, offerId },
          {
            onSuccess: () => {
              showToast({
                message: active
                  ? `${offerLabel} deactivated successfully`
                  : `${offerLabel} activated successfully`,
                status: EToasterStatus.SUCCESS
              });
              getOffers.refetch();
            },
            onError: (e: unknown) => {
              const axiosError = e as {
                response?: { data?: { message?: string } };
              };
              showToast({
                message: `Error editing ${offerLabel.toLowerCase()}`,
                status: EToasterStatus.ERROR,
                description: axiosError.response?.data?.message ?? ''
              });
            }
          }
        );
      },
      onDuplicate: (offerId: string): void => {
        navigate(`./dup/${offerId}`);
      },
      onDelete: (offerId: string): void => {
        setSelectedOffer(offerId);
        setIsDeleteDialogOpen(true);
      }
    }),
    [navigate, toggleOfferStatus, getOffers, offerLabel]
  );

  // --- Navigation + modal callbacks ---

  const handleCreate = useCallback((): void => {
    navigate('./form');
  }, [navigate]);

  const handleCloseLimitedEditModal = useCallback((): void => {
    setShowLimitedEditModal(false);
  }, []);

  const handleCloseDeleteDialog = useCallback((): void => {
    setIsDeleteDialogOpen(false);
  }, []);

  const offers: Offer[] = useMemo((): Offer[] => {
    if (getOffers.isLoading || !getOffers.data) {
      return [];
    }

    const rawOffers = getOffers.data.offers ?? getOffers.data.result ?? [];

    return rawOffers.map((offer: Offer) => ({
      _id: offer.publisherOfferId,
      id: offer.offerId,
      ...offer
    }));
  }, [getOffers.isLoading, getOffers.data]);

  // --- Filter + sort helpers ---

  const filterOffers = useCallback(
    (offers: Offer[]): Offer[] => {
      return offers.filter((offer: Offer): boolean => {
        const lowerExternalId = offer.publisherOfferId.toLowerCase();
        const lowerName = offer.name.toLowerCase();
        return (
          lowerExternalId.includes(externalIdFilter.toLowerCase()) &&
          lowerName.includes(nameFilter.toLowerCase())
        );
      });
    },
    [externalIdFilter, nameFilter]
  );

  const applySortingToOffers = useCallback(
    (offers: Offer[]): Offer[] => applySorting(offers, sorting),
    [sorting]
  );

  // --- Offer pipeline ---

  const processOffers = useCallback(
    (offers: Offer[]): IProcessedOffers => {
      const filtered = filterOffers(offers);
      const sorted = applySortingToOffers(filtered);
      const totalCount = getOffers.data?.totalCount || 0;
      const totalPages = Math.ceil(totalCount / rowsPerPage);
      const buttonText = `${offers.length > 0 ? 'Add' : 'Create'} New ${offerLabel}`;
      return { filtered, sorted, totalCount, totalPages, buttonText };
    },
    [
      filterOffers,
      applySortingToOffers,
      getOffers.data?.totalCount,
      rowsPerPage,
      offerLabel
    ]
  );

  return {
    getOffers,
    pricingsResult: getPricings.data?.result,
    isPricingsLoading: getPricings.isLoading,
    currentPage,
    setCurrentPage,
    rowsPerPage,
    sorting,
    updateSorting,
    externalIdFilter,
    setExternalIdFilter,
    nameFilter,
    setNameFilter,
    permissions,
    canManageOffers,
    canUserEdit,
    actions,
    selectedOffer,
    isDeleteDialogOpen,
    showLimitedEditModal,
    handleDeleteOffer,
    handleCreate,
    handleCloseLimitedEditModal,
    handleCloseDeleteDialog,
    navigate,
    toggleOfferStatus,
    deleteOffer,
    updateCollectedIndex,
    offers,
    processOffers
  };
};
